import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import DropdownIconsCheck from "../../../../parts/inputs/dropdown-icons-check";
import TablistNav from "../../../../parts/inputs/TablistNav";
import ToggleLinksInput from "../../../../parts/inputs/toggle-links-input";
import SvgIcon from "../../../../parts/SvgIcon";
import ChooseCoach from "../../../dashboard/settings/manage-coaches/ChooseCoach";
import { getUsersAssignableAsCoach } from "../../../dashboard/settings/manage-coaches/utils/manage-coaches-utils";
import SelectGroupsForMobileScreen from "./SelectGroupsForMobileScreen";
import SortGroupsBar, { SortIcon } from "./SortGroupsBar";

const getSortItems = (t) => [
  {
    title: t("athletes.header.sortAZ"),
    src: "/img/svg/vuesax-linear-tick-square.svg",
    alt: "asc",
    className: "grey",
    id: JSON.stringify([["name"], ["asc"]]),
  },
  {
    title: t("athletes.header.sortZA"),
    src: "/img/svg/vuesax-linear-tick-square.svg",
    alt: "desc",
    className: "grey",
    id: JSON.stringify([["name"], ["desc"]]),
  },
  {
    title: t("athletes.header.sortLastInvited"),
    src: "/img/svg/vuesax-linear-tick-square.svg",
    alt: "up",
    className: "grey",
    id: JSON.stringify([["joinedAt"], ["desc"]]),
  },
  {
    title: t("athletes.header.sortFirstInvited"),
    src: "/img/svg/vuesax-linear-tick-square.svg",
    alt: "down",
    className: "grey rotate",
    id: JSON.stringify([["joinedAt"], ["asc"]]),
  },
];

export const SubHeader = ({
  activeToggle,
  updateToggleActive,
  locals,
  referAsClients,
  isAllowToViewAllOrganizationsCoaches,
  listViewMode = "cards",
}) => {
  const { t } = useTranslation("coaching-portal");
  const {
    activeUser,
    usersOrganization = {
      organizationActiveUsers: [],
    },
    query,
  } = locals;
  const { organizationActiveUsers = [] } = usersOrganization;
  // A Team Coach never holds clients, so switching this view to "their" roster
  // would always land on an empty list — same reason it's filtered out of
  // every "assign a coach" picker.
  const assignableOrganizationActiveUsers = getUsersAssignableAsCoach(
    organizationActiveUsers
  );
  const selectOrganizationCoach = (e, coach) => {
    e.stopPropagation();
    e.preventDefault();
    submit(`/actions/refresh/?`, {
      method: "GET",
      body: {
        organizationCoach: coach._id,
        filterGroup: "",
      },
    });
  };
  const coach = query.organizationCoach
    ? organizationActiveUsers.find(
        (obj) => obj._id.toString() === query.organizationCoach.toString()
      )
    : activeUser;
  const [currentCoach, setCurrentCoach] = useState(coach);

  const isInactiveView = query?.filters?.["status.inactive"] ?? false;

  useEffect(() => {
    const updatedCoach = query.organizationCoach
      ? organizationActiveUsers.find(
          (obj) => obj._id.toString() === query.organizationCoach.toString()
        )
      : activeUser;

    if (updatedCoach) {
      setCurrentCoach(updatedCoach);
    }
  }, [query.organizationCoach]);
  return (
    <>
      {/* choose coach only for mobile */}
      {isAllowToViewAllOrganizationsCoaches && (
        <div className="force-only-show-mobile">
          <ChooseCoach
            activeUser={activeUser}
            organizationActiveUsers={assignableOrganizationActiveUsers}
            isFrom="list"
            onSelectCoach={selectOrganizationCoach}
            selectedCoach={currentCoach}
          />
        </div>
      )}
      <hgroup>
        <TablistNav
          name="filters[status.inactive]"
          defaultActive={activeToggle}
          options={[
            {
              text: t("athletes.header.all"),
              value: "all",
              href: "/actions/refresh?filters[status.inactive]=all&&name=",
            },
            {
              text: t("athletes.header.active"),
              value: false,
              dataStatus: "active",
            },
            {
              text: t("athletes.header.inactive"),
              value: true,
              dataStatus: "inactive",
            },
          ]}
          callback={(value) => {
            if (value === true && listViewMode === "config-list") {
              submit("/actions/refresh", {
                method: "GET",
                body: {
                  "filters[status.inactive]": "true",
                  showConfigList: "",
                },
              });
              return;
            }
            updateToggleActive(value);
          }}
        />
        {!isInactiveView && (
          <div
            className="athlete-list-layout-toggle"
            role="tablist"
            aria-label={t("athletes.header.layoutAriaLabel")}
          >
            <button
              type="button"
              role="tab"
              aria-selected={listViewMode === "cards"}
              data-button-type="secondary"
              onClick={() =>
                submit("/actions/refresh", {
                  method: "GET",
                  body: { showConfigList: "" },
                })
              }
            >
              <SvgIcon src="/img/new-icons/grid-outline.svg" alt="grid" />
              {t("athletes.header.cardsTab")}
            </button>

            <button
              type="button"
              role="tab"
              aria-selected={listViewMode === "config-list"}
              data-button-type="secondary"
              onClick={() =>
                submit("/actions/refresh", {
                  method: "GET",
                  body: { showConfigList: "1" },
                })
              }
            >
              <SvgIcon
                src="/img/new-icons/settings-light.svg"
                alt={t("athletes.header.configurationAlt")}
              />
              {t("athletes.header.configurationTab")}
            </button>
          </div>
        )}
        {/* desktop */}
        <DropdownIconsCheck
          title={t("athletes.header.sort")}
          items={getSortItems(t)}
          classNames="mobile-hidden"
          action={(sort) => {
            submit(`/actions/refresh`, {
              method: "GET",
              body: { sort },
            });
          }}
        />
        <div className="watch-organization-coaches-for-athletes">
          {isAllowToViewAllOrganizationsCoaches && (
            <>
              {/* Only to desktop */}
              <ChooseCoach
                classNames="force-mobile-hidden"
                activeUser={activeUser}
                organizationActiveUsers={assignableOrganizationActiveUsers}
                isFrom="list"
                onSelectCoach={selectOrganizationCoach}
                selectedCoach={currentCoach}
              />
            </>
          )}
          <a
            role="button"
            data-button-type="primary"
            data-icon
            href={`/coaching-portal/athletes/?modal[type]=invite-athlete`}
          >
            <SvgIcon
              src="/img/new-icons/add.svg"
              alt={t("athletes.header.addRoleAlt", {
                role: referAsClients
                  ? t("athletes.roleClientLower")
                  : t("athletes.roleAthleteLower"),
              })}
              noFilterOnLight={true}
            />
            {t("athletes.header.addRole", {
              role: referAsClients
                ? t("athletes.roleClient")
                : t("athletes.roleAthlete"),
            })}
          </a>
        </div>
      </hgroup>
    </>
  );
};

export const Header = ({
  athletesGroups = [],
  referAsClients,
  isAllowToViewAllOrganizationsCoaches,
  hideOrganizationFilter = false,
  canSortGroups = false,
  query,
}) => {
  const { t } = useTranslation("coaching-portal");
  const athletesArrayGroups = athletesGroups || [];
  const [sortingGroups, setSortingGroups] = useState(false);

  const clientWord = referAsClients
    ? t("athletes.roleClients")
    : t("athletes.roleAthletes");

  const showOrganizationFilter =
    isAllowToViewAllOrganizationsCoaches && !hideOrganizationFilter;

  const groupOptions = showOrganizationFilter
    ? [
        {
          text: t("athletes.header.allRole", { role: clientWord }),
          value: "all",
        },
        {
          text: t("athletes.header.allOrganizationRole", { role: clientWord }),
          value: "organization",
          href: "/actions/refresh?filterGroup=organization",
        },
      ]
    : [
        {
          text: t("athletes.header.allRole", { role: clientWord }),
          value: "all",
        },
      ];

  return (
    <div className="head athletes-list-header">
      <div className="left-side">
        {/* <div className="athletes-groups-bar" style={{ display: "none" }}> */}
        <div className="athletes-groups-bar">
          {((athletesArrayGroups && athletesArrayGroups.length > 0) ||
            isAllowToViewAllOrganizationsCoaches) && (
            <>
              {/* desktop */}
              {sortingGroups ? (
                <SortGroupsBar
                  athletesGroups={athletesArrayGroups}
                  onClose={() => setSortingGroups(false)}
                />
              ) : (
                <ToggleLinksInput
                  className="force-mobile-hidden"
                  name="filterGroup"
                  options={[
                    ...groupOptions,
                    ...athletesArrayGroups.map((athleteGroup) => {
                      return {
                        text: athleteGroup.name,
                        value: athleteGroup.name,
                      };
                    }),
                    {
                      text: t("athletes.header.addGroup"),
                      value: "add-group",
                      href: `/coaching-portal/athletes/?modal[type]=new-group`,
                      IconImg: (
                        <SvgIcon
                          src="/img/new-icons/add.svg"
                          alt={t("athletes.header.addGroupAlt")}
                        />
                      ),
                    },
                  ]}
                >
                  {canSortGroups && athletesArrayGroups.length > 1 && (
                    <button
                      type="button"
                      className="athletes-sort-groups-toggle"
                      onClick={() => setSortingGroups(true)}
                    >
                      <SortIcon />
                      {t("athletes.header.sortGroups")}
                    </button>
                  )}
                </ToggleLinksInput>
              )}
              {/* mobile */}
              <SelectGroupsForMobileScreen
                clientWord={clientWord}
                classNames="force-only-show-mobile"
                query={query}
                options={[
                  ...groupOptions,
                  ...athletesArrayGroups.map((athleteGroup) => {
                    return {
                      text: athleteGroup.name,
                      value: athleteGroup.name,
                    };
                  }),
                ]}
              />
              <DropdownIconsCheck
                title={t("athletes.header.sort")}
                items={getSortItems(t)}
                classNamesPositions="modal-position-left-mobile"
                classNames="only-show-mobile"
                action={(sort) => {
                  submit(`/actions/refresh`, {
                    method: "GET",
                    body: { sort },
                  });
                }}
              />
            </>
          )}
          {athletesArrayGroups.length === 0 &&
            !isAllowToViewAllOrganizationsCoaches && (
              <a
                role="button"
                data-button-type="secondary"
                href="/coaching-portal/athletes/?modal[type]=new-group"
              >
                <SvgIcon
                  src="/img/new-icons/add.svg"
                  alt={t("athletes.header.addGroupAlt")}
                  noFilterOnLight={true}
                />{" "}
                {t("athletes.header.addGroup")}
              </a>
            )}
        </div>
      </div>
      <div className="right-side" />
    </div>
  );
};
